/* eslint no-param-reassign: ['error', { 'props': false }] */
/* eslint no-shadow: 0 */

import { get, isNil } from 'lodash';
import {
  login,
  facebookSSOLogin,
  googleSSOLogin,
  emailLogin,
  userTokenCheck,
  userAnonymousLogin,
  getUserId,
} from '@/api';
import { loadState, saveState } from '@/utils/storage';
import FingerPrint from 'fingerprintjs2';
import { LOGIN, LOGOUT, TOKEN_VALIDATION, SET_USER_ID, SET_FLASH } from '@/store/mutation-types';


const state = {
  details: {},
  flash_message: {},
  user_id: null,
  validatingToken: true,
  authenticating: false,
};

const getters = {
  authenticating(state) {
    return state.authenticating;
  },
  isNewUser(state) {
    return get(state, 'details.new_user_flag') === 1;
  },
  userToken(state) {
    return state.details.user_token;
  },
  validatingToken(state) {
    return state.validatingToken;
  },
  userId(state) {
    return state.user_id;
  },
  flashMessage(state) {
    return state.flash_message || {};
  },
};

const mutations = {
  AUTHENTICATING(state, value) {
    state.authenticating = value;
  },
  [LOGIN](state, values) {
    state.details = values.details || {};
  },
  [LOGOUT](state) {
    state.details = {};
  },
  [SET_USER_ID](state, userId) {
    state.user_id = userId;
  },
  [TOKEN_VALIDATION](state, { validatingToken, userToken }) {
    state.validatingToken = validatingToken;
    state.details = { user_token: userToken };
  },
  [SET_FLASH](state, triggeredFlashMessages) {
    state.flash_message = { ...triggeredFlashMessages };
  },
  ONBOARDING_COMPLETE(state) {
    state.details.new_user_flag = 0;
    state.details = { ...state.details };
  },
};

const actions = {
  onboardingComplete({ commit }) {
    commit('ONBOARDING_COMPLETE');
  },
  setFlashMessage({ commit }, value) {
    commit(SET_FLASH, value);
  },
  userLogin({ commit }, params) {
    // const { router, redirectUrl } = params;
    commit('AUTHENTICATING', true);
    login(params)
      .then((response) => {
        commit('AUTHENTICATING', false);
        if (get(response, 'data[1].status') === 'SUCCESS') {
          // const details = get(response, 'data[1].data.details');
          // commit(LOGIN, { details });
          // saveState(details.user_token, 'user_token');
          commit(SET_FLASH, { message: 'E-Mail Login link has been sent successfully.', type: 'success' });
          // router.push(redirectUrl);
        } else {
          const error = get(response, 'data[1].error.error_usr_msg');
          commit(LOGIN, { });
          commit(SET_FLASH, { message: error, type: 'error' });
        }
      })
      .catch((response) => {
        commit('AUTHENTICATING', false);
        const error = response.data[1];
        commit(LOGIN, { });
        commit(SET_FLASH, { message: error, type: 'error' });
      });
  },
  userLogout({ commit }, { router }) {
    saveState(null, 'user_token');
    commit(LOGOUT);
    router.push({ name: 'Login' });
  },
  facebookLogin({ commit }, { token, router, redirectUrl }) {
    facebookSSOLogin(token)
      .then((response) => {
        if (get(response, 'data[1].status') === 'SUCCESS') {
          const details = get(response, 'data[1].data.details');
          commit(LOGIN, { details });
          saveState(details.user_token, 'user_token');
          router.push(redirectUrl);
        } else {
          const error = get(response, 'data[1].error');
          commit(LOGIN, { });
          commit(SET_FLASH, { message: error, type: 'error' });
        }
      })
      .catch(() => {
        const error = 'Error when logging in via Facebook';
        commit(LOGIN, { });
        commit(SET_FLASH, { message: error, type: 'error' });
      });
  },
  googleLogin({ commit }, { token, router, redirectUrl }) {
    googleSSOLogin(token)
      .then((response) => {
        const details = get(response, 'data[1].data.details');
        commit(LOGIN, { details });
        saveState(details.user_token, 'user_token');
        router.push(redirectUrl);
      })
      .catch(() => {
        const error = 'Error when logging in via Google';
        commit(LOGIN, { });
        commit(SET_FLASH, { message: error, type: 'error' });
      });
  },
  emailLogin({ commit }, { token, router, redirectUrl }) {
    emailLogin(token)
      .then((response) => {
        if (get(response, 'data[1].status') === 'SUCCESS') {
          const details = get(response, 'data[1].data.details');
          commit(LOGIN, { details });
          saveState(details.user_token, 'user_token');
          router.push(redirectUrl);
        } else {
          const error = get(response, 'data[1].error.error_usr_msg');
          commit(LOGIN, { });
          commit(SET_FLASH, { message: error, type: 'error' });
        }
      })
      .catch(() => {
        const error = 'Error when logging in to your account.';
        commit(LOGIN, { });
        commit(SET_FLASH, { message: error, type: 'error' });
      });
  },

  loginAnonymously({ commit }) {
    return new Promise((resolve, reject) => {
      new FingerPrint().get((hash) => {
        userAnonymousLogin(hash)
          .then(({ data }) => {
            if (get(data, '[1].status') === 'SUCCESS') {
              const details = get(data, '[1].data.details');
              commit(LOGIN, { details });
              resolve();
            } else {
              const error = get(data, '[1].error.error_usr_msg');
              commit(LOGIN, { });
              commit(SET_FLASH, { message: error, type: 'error' });
              reject();
            }
          });
      });
    });
  },
  checkToken({ commit }, { router, route }) {
    const userToken = loadState('user_token');
    // if user token in empty mark validation complete and redirec to login
    if (isNil(userToken)) {
      commit(TOKEN_VALIDATION, { validatingToken: false });
      router.push(route.query.redirect || 'login');
      return;
    }

    // in case the token is present, let's verify if it works right
    userTokenCheck(userToken)
      .then(({ data }) => {
        const status = get(data, '[1].status');
        if (status === 'SUCCESS') {
          // add the user token to the state
          commit(TOKEN_VALIDATION, { validatingToken: false, userToken });
          router.push(route.query.redirect || 'effort');
        }
      })
      .catch(() => {
        const error = 'Your login has expired. Please login again.';
        saveState(null, 'user_token');
        commit(SET_FLASH, { message: error, type: 'error' });
        commit(TOKEN_VALIDATION, { validatingToken: false });
      });
  },

  getUserId({ commit }) {
    const userToken = loadState('user_token');
    return getUserId(userToken)
      .then(({ data }) => {
        const userId = get(data, '[1].data.details.user_id');
        commit(SET_USER_ID, userId);
      })
      .catch(() => {
        commit(SET_FLASH, { message: 'Something went wrong', type: 'error' });
      });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};



// WEBPACK FOOTER //
// ./src/store/modules/auth.js